import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  IconButton,
  Divider,
  useTheme,
  Theme,
  Grid2,
  InputAdornment,
  Alert,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  ShipmentMethod,
  ShipmentsDetails,
  SHIPMENT_BOOKING_TYPE_OPTIONS,
  formatShipmentBookingTypeLabel,
  parseDeclaredValue,
  parseShipmentBookingType,
} from "../../../../../models/Shipments";
import { useStores } from "../../../use-stores";
import dayjs from "dayjs";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { ShipmentStatusEnum } from "../../../../../models/Shipments/ShipmentStatus";
import DatePicker2 from "../../../../../views/components/common/textFields/DatePicker2";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import CarriersSearchDropdown from "./CarriersSearchDropdown";
import LabelSecondary from "./LabelSecondary";
import {
  getShipmentSectionApiErrorMessage,
  ShipmentDetailsApiRow,
  UpdateShipmentAddressRequest,
  UpdateShipmentDeliveryRequest,
  UpdateShipmentDeliveryResult,
  UpdateShipmentRecipientRequest,
} from "../../../../../models/Shipments/ShipmentDetails";
import {
  getBookingSyncErrorMessage,
  isStaleInProgressBooking,
} from "../../../../../models/Shipments/ShipmentBooking";
import {
  formatRetryAfter,
  getTrackingSyncErrorMessage,
  isTrackingTerminalStatus,
} from "../../../../../models/Shipments/ShipmentTrackingSync";
import ShipmentsAPI from "../../../api/ShipmentsAPI";
import ShipmentBoxesSection from "../../pages/shipments/ShipmentBoxesSection";
import ShipmentProductsSection from "../../pages/shipments/ShipmentProductsSection";
import {
  canAddShipmentInventoryLines,
  canRemoveShipmentInventoryLines,
} from "../../../helpers/shipmentInventoryLines.helper";

interface EditShipmentModalProps {
  open: boolean;
  onClose: () => void;
  shipmentId: number | null;
}

type SectionFeedback = {
  severity: "success" | "error";
  text: string;
} | null;

const STATUS_OPTIONS = [
  { id: 1, value: "New" },
  { id: 2, value: "Ready To Ship" },
  { id: 3, value: "Shipped" },
  { id: 4, value: "Out For Delivery" },
  { id: 5, value: "Delivered" },
  { id: 6, value: "Return Requested" },
];

const SHIPMENT_METHOD_OPTIONS: ShipmentMethod[] = [
  ShipmentMethod.RAIL,
  ShipmentMethod.SURFACE,
  ShipmentMethod.AIR,
];

const textFieldSx = {
  "& .MuiInputLabel-root": { fontSize: "12px" },
  "& .MuiInputBase-input": { fontSize: "12px" },
};

const parseShipmentMethod = (
  value?: string | null,
): ShipmentMethod | undefined => {
  try {
    if (!value || !String(value).trim()) {
      return undefined;
    }

    const normalized = String(value).trim().toUpperCase();
    if (normalized === ShipmentMethod.RAIL) {
      return ShipmentMethod.RAIL;
    }
    if (normalized === ShipmentMethod.SURFACE) {
      return ShipmentMethod.SURFACE;
    }
    if (normalized === ShipmentMethod.AIR) {
      return ShipmentMethod.AIR;
    }

    return undefined;
  } catch {
    return undefined;
  }
};

const toDateOnlyString = (value?: Date | string | null): string | undefined => {
  try {
    if (value == null || value === "") {
      return undefined;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : undefined;
  } catch {
    return undefined;
  }
};

const toDateTimeLocalString = (
  value?: Date | string | null,
): string | undefined => {
  try {
    if (value == null || value === "") {
      return undefined;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : undefined;
  } catch {
    return undefined;
  }
};

const requiredTrimmed = (value: unknown): string => String(value ?? "").trim();

const formatBookingDate = (value?: Date | string | null): string => {
  try {
    if (!value) {
      return "—";
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
  } catch {
    return "—";
  }
};

const isBookedState = (bookingState?: string | null) =>
  String(bookingState || "").toUpperCase() === "BOOKED";

const isShippedOrLaterStatus = (status?: number | null) =>
  status != null &&
  status >= ShipmentStatusEnum.SHIPPED &&
  status !== ShipmentStatusEnum.CANCELLED;

const TERMINAL_SHIPMENT_STATUSES = new Set<number>([
  ShipmentStatusEnum.DELIVERED,
  ShipmentStatusEnum.RETURNED,
  ShipmentStatusEnum.CANCELLED,
  ShipmentStatusEnum.UNDELIVERED,
  ShipmentStatusEnum.DAMAGED,
]);

const isTerminalShipmentStatus = (status?: number | null) =>
  status != null && TERMINAL_SHIPMENT_STATUSES.has(status);

/** Delivery PUT still accepts status + AWB / tracking URL on these (RTO re-ships). */
const TRACKING_EDITABLE_STATUSES = new Set<number>([
  ShipmentStatusEnum.SHIPPED,
  ShipmentStatusEnum.OUT_FOR_DELIVERY,
  ShipmentStatusEnum.DELIVERED,
  ShipmentStatusEnum.RETURN_REQUESTED,
  ShipmentStatusEnum.RETURNED,
]);

const isTrackingEditableStatus = (status?: number | null) =>
  status != null && TRACKING_EDITABLE_STATUSES.has(status);

const isRtoStatus = (status?: number | null) =>
  status === ShipmentStatusEnum.RETURN_REQUESTED ||
  status === ShipmentStatusEnum.RETURNED;

const isRtoReshipStatus = (status?: number | null) =>
  status === ShipmentStatusEnum.SHIPPED ||
  status === ShipmentStatusEnum.DELIVERED;

const getBookingStateChipColor = (
  bookingState?: string | null,
): "success" | "error" | "warning" | "default" => {
  switch (String(bookingState || "").toUpperCase()) {
    case "BOOKED":
      return "success";
    case "FAILED":
      return "error";
    case "QUEUED":
    case "IN_PROGRESS":
      return "warning";
    default:
      return "default";
  }
};

const getLoadErrorMessage = (error: unknown): string => {
  try {
    if (error && typeof error === "object") {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      return (
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch shipment details. Please try again."
      );
    }
    return "Failed to fetch shipment details. Please try again.";
  } catch {
    return "Failed to fetch shipment details. Please try again.";
  }
};

const mapShipmentDetailsApiRowToForm = (
  row: ShipmentDetailsApiRow,
): ShipmentsDetails => {
  try {
    return {
      id: row.id,
      first_name: row.first_name || "",
      last_name: row.last_name || "",
      email: row.email || "",
      order_notes: row.order_notes || "",
      address1: row.address1 || "",
      address2: row.address2 || "",
      landmark: row.landmark || "",
      country: row.country || "",
      state: row.state || "",
      city: row.city || "",
      pincode: row.pincode || "",
      phone: row.phone || "",
      alternate_phone: row.alternate_phone || "",
      expected_delivery_date: toDateOnlyString(row.expected_delivery_date),
      dispatch_date: toDateTimeLocalString(row.dispatch_date),
      delivery_partner_id: row.delivery_partner_id ?? undefined,
      delivery_partner_name: row.delivery_partner_name || "",
      status: row.status ?? undefined,
      status_label: row.status_label || "",
      awb: row.awb || "",
      awb_track_url: row.awb_track_url || "",
      shipping_cost: row.shipping_cost != null ? row.shipping_cost : undefined,
      shipment_method: parseShipmentMethod(row.shipment_method),
      booking_type: parseShipmentBookingType(row.booking_type),
      declared_value: row.declared_value != null ? row.declared_value : null,
    };
  } catch {
    throw new Error("Failed to map shipment details");
  }
};

const parseShippingCost = (
  value: unknown,
): { ok: true; cost: number | null } | { ok: false; message: string } => {
  try {
    if (value === "" || value == null) {
      return { ok: true, cost: null };
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return { ok: false, message: "Shipping cost is invalid." };
    }
    return { ok: true, cost: parsed };
  } catch {
    return { ok: false, message: "Shipping cost is invalid." };
  }
};

const EditShipmentModal: React.FC<EditShipmentModalProps> = ({
  open,
  onClose,
  shipmentId,
}) => {
  const theme = useTheme<Theme>();
  const { shipmentStore, userStore } = useStores();
  const api = useMemo(() => new ShipmentsAPI(), []);
  const requestSeqRef = useRef(0);
  const [details, setDetails] = useState<ShipmentDetailsApiRow | null>(null);
  const [shipmentDetails, setShipmentDetails] = useState<
    ShipmentsDetails | undefined
  >(undefined);
  const [statusList, setStatusList] = useState(STATUS_OPTIONS);
  const [linkCopied, setLinkCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [recipientFeedback, setRecipientFeedback] =
    useState<SectionFeedback>(null);
  const [addressFeedback, setAddressFeedback] = useState<SectionFeedback>(null);
  const [deliveryFeedback, setDeliveryFeedback] =
    useState<SectionFeedback>(null);
  const [bookingFeedback, setBookingFeedback] = useState<SectionFeedback>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [savingRecipient, setSavingRecipient] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [syncingBooking, setSyncingBooking] = useState(false);
  const [trackingFeedback, setTrackingFeedback] =
    useState<SectionFeedback>(null);
  const [syncingTracking, setSyncingTracking] = useState(false);
  const [nextTrackingSyncAt, setNextTrackingSyncAt] = useState<string | null>(
    null,
  );
  const isAnySectionSaving =
    savingRecipient ||
    savingAddress ||
    savingDelivery ||
    syncingBooking ||
    syncingTracking;

  const applyLoadedShipmentRow = (row: ShipmentDetailsApiRow) => {
    try {
      setDetails(row);
      setShipmentDetails(mapShipmentDetailsApiRowToForm(row));
      setStatusList(
        row.status === ShipmentStatusEnum.CANCELLED
          ? [
              ...STATUS_OPTIONS,
              {
                id: ShipmentStatusEnum.CANCELLED,
                value: "Cancelled",
              },
            ]
          : row.status === ShipmentStatusEnum.RETURNED
            ? [
                ...STATUS_OPTIONS,
                {
                  id: ShipmentStatusEnum.RETURNED,
                  value: "Returned",
                },
              ]
            : STATUS_OPTIONS,
      );
    } catch {
      throw new Error("Failed to apply shipment details.");
    }
  };

  useEffect(() => {
    if (!open || shipmentId == null) {
      requestSeqRef.current += 1;
      setDetails(null);
      setShipmentDetails(undefined);
      setLoading(false);
      setLoadError(null);
      setRefreshing(false);
      setRefreshError(null);
      setRecipientFeedback(null);
      setAddressFeedback(null);
      setDeliveryFeedback(null);
      setBookingFeedback(null);
      setTrackingFeedback(null);
      setNextTrackingSyncAt(null);
      setSavingRecipient(false);
      setSavingAddress(false);
      setSavingDelivery(false);
      setSyncingBooking(false);
      setSyncingTracking(false);
      setLinkCopied(false);
      setCopyError(null);
      setStatusList(STATUS_OPTIONS);
      return;
    }

    const seq = ++requestSeqRef.current;
    setLoading(true);
    setLoadError(null);
    setRefreshError(null);
    setRecipientFeedback(null);
    setAddressFeedback(null);
    setDeliveryFeedback(null);
    setBookingFeedback(null);
    setTrackingFeedback(null);
    setNextTrackingSyncAt(null);
    setDetails(null);
    setShipmentDetails(undefined);

    const loadDetails = async () => {
      try {
        const response = await api.getShipmentDetails(shipmentId);
        if (seq !== requestSeqRef.current) {
          return;
        }
        if (!response?.success) {
          throw new Error(
            response?.message || "Failed to fetch shipment details",
          );
        }

        applyLoadedShipmentRow(response.result as ShipmentDetailsApiRow);
      } catch (error: unknown) {
        if (seq !== requestSeqRef.current) {
          return;
        }
        setDetails(null);
        setShipmentDetails(undefined);
        setLoadError(getLoadErrorMessage(error));
      } finally {
        if (seq !== requestSeqRef.current) {
          return;
        }
        setLoading(false);
      }
    };

    void loadDetails();
  }, [open, shipmentId, reloadToken, api]);

  const isDetailsLoading =
    loading || (Boolean(open) && shipmentId != null && !details && !loadError);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setShipmentDetails((prev) =>
      prev ? { ...prev, [e.target.name]: e.target.value } : prev,
    );
  };

  const canEditShipments = userStore.hasPermission(
    EmployeeRoles.EDIT_SHIPMENTS,
    true,
  );

  const persistedStatus = details?.status ?? null;
  const isCancelled = persistedStatus === ShipmentStatusEnum.CANCELLED;
  const isBooked = isBookedState(details?.booking_state);
  const isTerminalStatus = isTerminalShipmentStatus(persistedStatus);
  const isTrackingEditable = isTrackingEditableStatus(persistedStatus);
  const isRto = isRtoStatus(persistedStatus);
  // DELIVERED / RETURNED are terminal for every other section, not for delivery.
  const isDeliveryLocked = isTerminalStatus && !isTrackingEditable;
  const isStatusOnlyLock =
    !isTerminalStatus && (isBooked || isShippedOrLaterStatus(persistedStatus));
  const isTrackingOnlyLock = isTrackingEditable && !isRto;
  const canEditRecipient =
    canEditShipments &&
    persistedStatus === ShipmentStatusEnum.NEW &&
    !isStatusOnlyLock &&
    !isTerminalStatus;
  const canEditAddress =
    canEditShipments &&
    persistedStatus === ShipmentStatusEnum.NEW &&
    !isStatusOnlyLock &&
    !isTerminalStatus;
  const canEditDelivery =
    canEditShipments && !isCancelled && !isStatusOnlyLock && !isTerminalStatus;
  const canEditStatus = canEditShipments && !isDeliveryLocked;
  const canEditShippingCost =
    canEditDelivery || (canEditShipments && isTrackingOnlyLock);
  const canEditDeliveryDates =
    canEditDelivery && persistedStatus === ShipmentStatusEnum.NEW;
  const canEditPartnerMethodBooking =
    canEditDelivery &&
    (persistedStatus === ShipmentStatusEnum.NEW ||
      persistedStatus === ShipmentStatusEnum.READY_TO_SHIP);
  const canEditDeliveryAwb =
    canEditDelivery || (canEditShipments && isTrackingEditable);
  const requeueStaleInProgress = isStaleInProgressBooking(details);
  const showCarrierBooking =
    persistedStatus != null && persistedStatus !== ShipmentStatusEnum.NEW;
  const showLastBookingError = Boolean(details?.last_booking_error);
  const canSyncTracking = canEditShipments && isBooked;

  const canEditBoxes =
    !isStatusOnlyLock &&
    !isTerminalStatus &&
    (details?.status === ShipmentStatusEnum.NEW ||
      details?.status === ShipmentStatusEnum.READY_TO_SHIP) &&
    userStore.hasPermission(EmployeeRoles.EDIT_SHIPMENTS);

  const canEditShipmentLines = userStore.hasPermission(
    EmployeeRoles.EDIT_SHIPMENTS,
  );
  const canEditTerminalLines = userStore.hasPermission(
    EmployeeRoles.EDIT_ITEMS_FROM_TERMINAL_SHIPMENTS,
  );
  const canAddItems = canAddShipmentInventoryLines({
    status: details?.status,
    hasEditShipments: canEditShipmentLines,
    hasEditTerminalLines: canEditTerminalLines,
  });
  const canRemoveProducts = canRemoveShipmentInventoryLines({
    status: details?.status,
    shipmentType: details?.shipment_type,
    hasEditShipments: canEditShipmentLines,
    hasEditTerminalLines: canEditTerminalLines,
  });

  const canMovePastNew = Boolean(
    shipmentDetails?.delivery_partner_id &&
    Number(shipmentDetails.delivery_partner_id) > 0 &&
    shipmentDetails?.shipment_method,
  );

  const statusDropdownOptions = (
    canMovePastNew
      ? statusList
      : statusList.filter(
          (status) =>
            status.id === ShipmentStatusEnum.NEW ||
            status.id === shipmentDetails?.status,
        )
  ).map((status) => ({
    id: String(status.id),
    value: String(status.value),
  }));

  const handleClose = () => {
    try {
      if (isAnySectionSaving) {
        return;
      }
      onClose();
    } catch {
      // Keep the dialog open if close handling fails mid-save.
    }
  };

  const buildRecipientPayload = ():
    | { ok: true; payload: UpdateShipmentRecipientRequest }
    | { ok: false; message: string } => {
    try {
      if (!shipmentDetails) {
        return { ok: false, message: "Shipment details are not loaded." };
      }

      const first_name = requiredTrimmed(shipmentDetails.first_name);
      const email = requiredTrimmed(shipmentDetails.email);
      const phone = requiredTrimmed(shipmentDetails.phone);
      if (!first_name || !email || !phone) {
        return {
          ok: false,
          message: "First name, email, and phone are required.",
        };
      }

      return {
        ok: true,
        payload: {
          first_name,
          last_name: String(shipmentDetails.last_name ?? ""),
          email,
          phone,
          alternate_phone: String(shipmentDetails.alternate_phone ?? ""),
        },
      };
    } catch {
      return { ok: false, message: "Failed to prepare recipient details." };
    }
  };

  const buildAddressPayload = ():
    | { ok: true; payload: UpdateShipmentAddressRequest }
    | { ok: false; message: string } => {
    try {
      if (!shipmentDetails) {
        return { ok: false, message: "Shipment details are not loaded." };
      }

      const address1 = requiredTrimmed(shipmentDetails.address1);
      const city = requiredTrimmed(shipmentDetails.city);
      const state = requiredTrimmed(shipmentDetails.state);
      const country = requiredTrimmed(shipmentDetails.country);
      const pincode = requiredTrimmed(shipmentDetails.pincode);
      if (!address1 || !city || !state || !country || !pincode) {
        return {
          ok: false,
          message:
            "Address line 1, city, state, country, and pincode are required.",
        };
      }

      return {
        ok: true,
        payload: {
          address1,
          address2: String(shipmentDetails.address2 ?? ""),
          landmark: String(shipmentDetails.landmark ?? ""),
          city,
          state,
          country,
          pincode,
          order_notes: String(shipmentDetails.order_notes ?? ""),
        },
      };
    } catch {
      return { ok: false, message: "Failed to prepare shipping address." };
    }
  };

  const buildDeliveryPayload = ():
    | { ok: true; payload: UpdateShipmentDeliveryRequest }
    | { ok: false; message: string } => {
    try {
      if (!shipmentDetails) {
        return { ok: false, message: "Shipment details are not loaded." };
      }
      if (isDeliveryLocked) {
        return {
          ok: false,
          message: "Cannot edit a shipment in a terminal status.",
        };
      }
      if (isCancelled && !isStatusOnlyLock) {
        return { ok: false, message: "Cannot edit cancelled shipment." };
      }

      const status = Number(shipmentDetails.status);
      if (!Number.isFinite(status) || status <= 0) {
        return { ok: false, message: "Status is required." };
      }

      const payload: UpdateShipmentDeliveryRequest = { status };

      if (isRto) {
        if (!isRtoReshipStatus(status)) {
          return { ok: true, payload };
        }
        const awb = requiredTrimmed(shipmentDetails.awb);
        const awbTrackUrl = requiredTrimmed(shipmentDetails.awb_track_url);
        if (!awb || !awbTrackUrl) {
          return {
            ok: false,
            message:
              "AWB and tracking link are required to re-ship a returned shipment.",
          };
        }
        payload.awb = awb;
        payload.awb_track_url = awbTrackUrl;
        return { ok: true, payload };
      }

      if (isTrackingOnlyLock) {
        const costResult = parseShippingCost(shipmentDetails.shipping_cost);
        if (!costResult.ok) {
          return { ok: false, message: costResult.message };
        }
        const awb = requiredTrimmed(shipmentDetails.awb);
        const awbTrackUrl = requiredTrimmed(shipmentDetails.awb_track_url);
        if (
          persistedStatus === ShipmentStatusEnum.DELIVERED &&
          (!awb || !awbTrackUrl)
        ) {
          return {
            ok: false,
            message:
              "AWB and tracking link cannot be cleared on a delivered shipment.",
          };
        }
        payload.shipping_cost = costResult.cost;
        payload.awb = awb || null;
        payload.awb_track_url = awbTrackUrl || null;
        return { ok: true, payload };
      }

      if (isStatusOnlyLock) {
        return { ok: true, payload };
      }
      const costResult = parseShippingCost(shipmentDetails.shipping_cost);
      if (!costResult.ok) {
        return { ok: false, message: costResult.message };
      }
      const declaredResult = parseDeclaredValue(shipmentDetails.declared_value);
      if (!declaredResult.ok) {
        return { ok: false, message: declaredResult.message };
      }

      payload.shipping_cost = costResult.cost;
      payload.declared_value = declaredResult.declaredValue;
      payload.awb = requiredTrimmed(shipmentDetails.awb) || null;
      payload.awb_track_url =
        requiredTrimmed(shipmentDetails.awb_track_url) || null;

      if (persistedStatus === ShipmentStatusEnum.NEW) {
        const expectedRaw = shipmentDetails.expected_delivery_date;
        if (expectedRaw == null || expectedRaw === "") {
          payload.expected_delivery_date = null;
        } else {
          const parsed = dayjs(expectedRaw);
          if (!parsed.isValid()) {
            return {
              ok: false,
              message: "Expected delivery date is invalid.",
            };
          }
          payload.expected_delivery_date = parsed.format("YYYY-MM-DD");
        }

        const dispatchRaw = shipmentDetails.dispatch_date;
        if (dispatchRaw == null || dispatchRaw === "") {
          payload.dispatch_date = null;
        } else {
          const parsed = dayjs(dispatchRaw);
          if (!parsed.isValid()) {
            return { ok: false, message: "Dispatch date is invalid." };
          }
          payload.dispatch_date = parsed.format("YYYY-MM-DD HH:mm:ss");
        }
      }

      if (
        persistedStatus === ShipmentStatusEnum.NEW ||
        persistedStatus === ShipmentStatusEnum.READY_TO_SHIP
      ) {
        const partnerId = Number(shipmentDetails.delivery_partner_id);
        if (Number.isFinite(partnerId) && partnerId > 0) {
          payload.delivery_partner_id = partnerId;
        }
        if (shipmentDetails.shipment_method) {
          payload.shipment_method = String(
            shipmentDetails.shipment_method,
          ).toUpperCase();
        }
        payload.booking_type =
          parseShipmentBookingType(shipmentDetails.booking_type) ?? null;
      }

      return { ok: true, payload };
    } catch {
      return {
        ok: false,
        message: "Failed to prepare delivery and tracking details.",
      };
    }
  };

  const applyDeliveryResult = (result: UpdateShipmentDeliveryResult) => {
    try {
      const nextPartnerId = result.delivery_partner_id ?? undefined;
      let nextPartnerName = "";
      if (nextPartnerId != null) {
        const samePartner =
          nextPartnerId === Number(shipmentDetails?.delivery_partner_id);
        nextPartnerName = samePartner
          ? shipmentDetails?.delivery_partner_name || ""
          : shipmentStore.deliveryPartners.find(
              (dp) => dp.delivery_partner_id === nextPartnerId,
            )?.delivery_partner_name ||
            shipmentDetails?.delivery_partner_name ||
            details?.delivery_partner_name ||
            "";
      }

      const nextStatusLabel =
        STATUS_OPTIONS.find((status) => status.id === result.status)?.value ||
        shipmentDetails?.status_label ||
        "";

      setShipmentDetails((prev) =>
        prev
          ? {
              ...prev,
              expected_delivery_date: toDateOnlyString(
                result.expected_delivery_date,
              ),
              dispatch_date: toDateTimeLocalString(result.dispatch_date),
              delivery_partner_id: nextPartnerId,
              delivery_partner_name: nextPartnerName,
              shipment_method: parseShipmentMethod(result.shipment_method),
              booking_type: parseShipmentBookingType(result.booking_type),
              status: result.status,
              status_label: nextStatusLabel,
              shipping_cost:
                result.shipping_cost != null ? result.shipping_cost : undefined,
              declared_value:
                result.declared_value !== undefined
                  ? result.declared_value
                  : prev.declared_value,
              awb: result.awb || "",
              awb_track_url: result.awb_track_url || "",
            }
          : prev,
      );
      setDetails((prev) =>
        prev
          ? {
              ...prev,
              expected_delivery_date: result.expected_delivery_date,
              dispatch_date: result.dispatch_date,
              delivery_partner_id: result.delivery_partner_id,
              delivery_partner_name: nextPartnerName || null,
              shipment_method: result.shipment_method,
              booking_type: result.booking_type,
              status: result.status,
              status_label: nextStatusLabel,
              shipping_cost: result.shipping_cost,
              declared_value:
                result.declared_value !== undefined
                  ? result.declared_value
                  : prev.declared_value,
              awb: result.awb,
              awb_track_url: result.awb_track_url,
            }
          : prev,
      );
    } catch {
      // Form stays as last successful payload if the slice cannot be merged.
    }
  };

  const handleSaveRecipient = async () => {
    try {
      if (shipmentId == null || !shipmentDetails || isDetailsLoading) {
        return;
      }
      if (isStatusOnlyLock) {
        setRecipientFeedback({
          severity: "error",
          text: "Only shipment status can be edited.",
        });
        return;
      }

      const prepared = buildRecipientPayload();
      if (!prepared.ok) {
        setRecipientFeedback({ severity: "error", text: prepared.message });
        return;
      }

      setSavingRecipient(true);
      setRecipientFeedback(null);

      const result = await shipmentStore.updateShipmentRecipient(
        shipmentId,
        prepared.payload,
        userStore,
      );
      setShipmentDetails((prev) =>
        prev
          ? {
              ...prev,
              first_name: result.first_name || "",
              last_name: result.last_name || "",
              email: result.email || "",
              phone: result.phone || "",
              alternate_phone: result.alternate_phone || "",
            }
          : prev,
      );
      setDetails((prev) =>
        prev
          ? {
              ...prev,
              first_name: result.first_name,
              last_name: result.last_name,
              email: result.email,
              phone: result.phone,
              alternate_phone: result.alternate_phone,
            }
          : prev,
      );
      setRecipientFeedback({
        severity: "success",
        text: "Recipient updated.",
      });
    } catch (error: unknown) {
      setRecipientFeedback({
        severity: "error",
        text: getShipmentSectionApiErrorMessage(
          error,
          "Failed to update recipient.",
        ),
      });
    } finally {
      setSavingRecipient(false);
    }
  };

  const handleSaveAddress = async () => {
    try {
      if (shipmentId == null || !shipmentDetails || isDetailsLoading) {
        return;
      }
      if (isStatusOnlyLock) {
        setAddressFeedback({
          severity: "error",
          text: "Only shipment status can be edited.",
        });
        return;
      }

      const prepared = buildAddressPayload();
      if (!prepared.ok) {
        setAddressFeedback({ severity: "error", text: prepared.message });
        return;
      }

      setSavingAddress(true);
      setAddressFeedback(null);

      const result = await shipmentStore.updateShipmentAddress(
        shipmentId,
        prepared.payload,
        userStore,
      );
      setShipmentDetails((prev) =>
        prev
          ? {
              ...prev,
              address1: result.address1 || "",
              address2: result.address2 || "",
              landmark: result.landmark || "",
              city: result.city || "",
              state: result.state || "",
              country: result.country || "",
              pincode: result.pincode || "",
              order_notes: result.order_notes || "",
            }
          : prev,
      );
      setDetails((prev) =>
        prev
          ? {
              ...prev,
              address1: result.address1,
              address2: result.address2,
              landmark: result.landmark,
              city: result.city,
              state: result.state,
              country: result.country,
              pincode: result.pincode,
              order_notes: result.order_notes,
            }
          : prev,
      );
      setAddressFeedback({
        severity: "success",
        text: "Shipping address updated.",
      });
    } catch (error: unknown) {
      setAddressFeedback({
        severity: "error",
        text: getShipmentSectionApiErrorMessage(
          error,
          "Failed to update shipping address.",
        ),
      });
    } finally {
      setSavingAddress(false);
    }
  };

  const handleSaveDelivery = async () => {
    try {
      if (shipmentId == null || !shipmentDetails || isDetailsLoading) {
        return;
      }

      const prepared = buildDeliveryPayload();
      if (!prepared.ok) {
        setDeliveryFeedback({ severity: "error", text: prepared.message });
        return;
      }

      setSavingDelivery(true);
      setDeliveryFeedback(null);

      const result = await shipmentStore.updateShipmentDelivery(
        shipmentId,
        prepared.payload,
        userStore,
      );
      applyDeliveryResult(result);
      setDeliveryFeedback({
        severity: "success",
        text:
          isRto && isRtoReshipStatus(result.status)
            ? "Returned shipment re-shipped with new tracking details."
            : isStatusOnlyLock && !isTrackingEditable
              ? "Shipment status updated."
              : "Delivery and tracking updated.",
      });
    } catch (error: unknown) {
      setDeliveryFeedback({
        severity: "error",
        text: getShipmentSectionApiErrorMessage(
          error,
          "Failed to update delivery and tracking.",
        ),
      });
    } finally {
      setSavingDelivery(false);
    }
  };

  const handleSyncBooking = async () => {
    try {
      if (shipmentId == null || !details) {
        setBookingFeedback({
          severity: "error",
          text: "Shipment details are not loaded.",
        });
        return;
      }

      if (details.status === ShipmentStatusEnum.NEW) {
        setBookingFeedback({
          severity: "error",
          text: "Carrier booking is available after the shipment is Ready to Ship.",
        });
        return;
      }
      if (isBookedState(details.booking_state)) {
        setBookingFeedback({
          severity: "error",
          text: "This shipment is already booked.",
        });
        return;
      }
      if (isTerminalShipmentStatus(details.status)) {
        setBookingFeedback({
          severity: "error",
          text: "This shipment is in a terminal status and cannot be booked.",
        });
        return;
      }

      setSyncingBooking(true);
      setBookingFeedback(null);

      const response = await api.syncShipmentBooking(shipmentId, {
        requeue: isStaleInProgressBooking(details),
      });
      if (!response?.success) {
        throw new Error(
          response?.message || "Failed to queue shipment booking.",
        );
      }

      setDetails((prev) =>
        prev
          ? {
              ...prev,
              booking_state: response.result?.booking_state || "QUEUED",
              carrier_ref_no:
                response.result?.carrier_ref_no || prev.carrier_ref_no,
            }
          : prev,
      );

      try {
        const refreshed = await api.getShipmentDetails(shipmentId);
        if (refreshed?.success && refreshed.result) {
          const row = refreshed.result;
          setDetails((prev) =>
            prev
              ? {
                  ...prev,
                  booking_state: row.booking_state,
                  booking_attempts: row.booking_attempts,
                  last_booked_at: row.last_booked_at,
                  last_booking_error: row.last_booking_error,
                  carrier_ref_no: row.carrier_ref_no,
                  booking_type: row.booking_type,
                  label_s3_key: row.label_s3_key,
                  mps_label_s3_key: row.mps_label_s3_key,
                }
              : prev,
          );
        }
      } catch {
        // Keep the sync result if a follow-up details refresh fails.
      }

      setBookingFeedback({
        severity: "success",
        text: response.message || "Shipment queued for carrier booking.",
      });
    } catch (error: unknown) {
      setBookingFeedback({
        severity: "error",
        text: getBookingSyncErrorMessage(error),
      });
    } finally {
      setSyncingBooking(false);
    }
  };

  const handleSyncTracking = async () => {
    try {
      if (shipmentId == null || !details) {
        setTrackingFeedback({
          severity: "error",
          text: "Shipment details are not loaded.",
        });
        return;
      }
      if (!isBookedState(details.booking_state)) {
        setTrackingFeedback({
          severity: "error",
          text: "Tracking can be synced only after the shipment is booked.",
        });
        return;
      }
      if (isTrackingTerminalStatus(details.status)) {
        setTrackingFeedback({
          severity: "error",
          text: "This shipment is in a final status, so tracking can't be synced.",
        });
        return;
      }
      if (nextTrackingSyncAt) {
        const waitMs = new Date(nextTrackingSyncAt).getTime() - Date.now();
        if (waitMs > 0) {
          setTrackingFeedback({
            severity: "error",
            text: `Tracking sync was requested recently. Try again in ${formatRetryAfter(waitMs / 1000)}.`,
          });
          return;
        }
      }

      setSyncingTracking(true);
      setTrackingFeedback(null);

      const response = await api.syncShipmentTracking(shipmentId);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to queue tracking sync.");
      }

      setNextTrackingSyncAt(response.result?.next_sync_allowed_at ?? null);
      setTrackingFeedback({
        severity: "success",
        text: `${response.message || "Shipment queued for tracking sync."} Refresh in a minute to see the latest status.`,
      });
    } catch (error: unknown) {
      setTrackingFeedback({
        severity: "error",
        text: getTrackingSyncErrorMessage(error),
      });
    } finally {
      setSyncingTracking(false);
    }
  };

  const handleRefreshDetails = async () => {
    const seq = ++requestSeqRef.current;
    try {
      if (shipmentId == null) {
        return;
      }

      setRefreshing(true);
      setRefreshError(null);
      setLoading(false);

      const response = await api.getShipmentDetails(shipmentId);
      if (seq !== requestSeqRef.current) {
        return;
      }
      if (!response?.success) {
        throw new Error(
          response?.message || "Failed to fetch shipment details",
        );
      }

      applyLoadedShipmentRow(response.result as ShipmentDetailsApiRow);
      setLoadError(null);
      setRecipientFeedback(null);
      setAddressFeedback(null);
      setDeliveryFeedback(null);
      setBookingFeedback(null);
      setTrackingFeedback(null);
    } catch (error: unknown) {
      if (seq !== requestSeqRef.current) {
        return;
      }
      const message = getLoadErrorMessage(error);
      if (details) {
        setRefreshError(message);
      } else {
        setLoadError(message);
      }
    } finally {
      if (seq === requestSeqRef.current) {
        setRefreshing(false);
      }
    }
  };

  const handleCopyTrackingLink = async () => {
    const url = shipmentDetails?.awb_track_url?.trim();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopyError(null);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setLinkCopied(false);
      setCopyError("Failed to copy tracking link");
    }
  };

  const sectionCtaStyles = { minHeight: 32, px: 1.5 };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors?.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors?.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            background: `linear-gradient(135deg, ${theme.palette.customColors?.white || "#fff"} 0%, ${theme.palette.customColors?.lightGreyColor || "#f5f5f5"} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors?.blueGradientStart || "#1976d2"} 0%, ${theme.palette.customColors?.blueGradientEnd || "#42a5f5"} 50%, ${theme.palette.customColors?.blueGradientStart || "#1976d2"} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors?.greyBorder || "#e0e0e0"}`,
          }}
        >
          <CustomTypography variant="heading4">
            {shipmentId != null
              ? `Edit Shipment #${shipmentId}`
              : "Edit Shipment"}
          </CustomTypography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {shipmentId != null && (
              <Tooltip title="Activity History">
                <span>
                  <IconButton
                    onClick={() => shipmentStore.openActivityLogs(shipmentId)}
                    disabled={isAnySectionSaving}
                    aria-label="Activity History"
                    sx={{
                      backgroundColor: theme.palette.primary.main + "10",
                      color: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.main + "20",
                      },
                    }}
                  >
                    <HistoryIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <AppButton
              label={refreshing ? "Refreshing…" : "Refresh"}
              variant="secondary"
              startIcon={<RefreshIcon />}
              onClick={() => {
                void handleRefreshDetails();
              }}
              disabled={
                refreshing ||
                isDetailsLoading ||
                isAnySectionSaving ||
                shipmentId == null
              }
              customStyles={sectionCtaStyles}
              fontSize="0.75rem"
            />
            <IconButton
              onClick={handleClose}
              disabled={isAnySectionSaving}
              sx={{
                color: theme.palette.customColors?.greyTextColor || "#666",
                "&:hover": {
                  backgroundColor: theme.palette.customColors?.lightGreyColor,
                  color: theme.palette.customColors?.blackBorder,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors?.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors?.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors?.greyTextColor,
              },
            },
          }}
        >
          {isDetailsLoading ? (
            <Box
              sx={{
                minHeight: 280,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <CircularProgress />
              <CustomTypography
                variant="body2"
                sx={{
                  color: theme.palette.customColors?.greyTextColor || "#666",
                }}
              >
                Loading shipment details…
              </CustomTypography>
            </Box>
          ) : loadError ? (
            <Box sx={{ py: 1 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {loadError}
              </Alert>
              <AppButton
                label="Retry"
                variant="secondary"
                onClick={() => setReloadToken((token) => token + 1)}
                customStyles={{ minHeight: 36, px: 1.5 }}
                fontSize="0.75rem"
              />
            </Box>
          ) : (
            <>
              {refreshError ? (
                <Alert
                  severity="error"
                  onClose={() => setRefreshError(null)}
                  sx={{ mb: 2 }}
                >
                  {refreshError}
                </Alert>
              ) : null}
              {isDeliveryLocked ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This shipment is in a terminal status and cannot be edited.
                </Alert>
              ) : isRto ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  To re-ship this returned shipment, set status to Shipped or
                  Delivered and enter the new AWB and tracking link.
                </Alert>
              ) : isTrackingOnlyLock ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Only status, shipping cost, AWB and tracking link can be
                  edited.
                </Alert>
              ) : isStatusOnlyLock ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Only shipment status can be edited.
                </Alert>
              ) : null}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 0.5,
                    flexWrap: "wrap",
                  }}
                >
                  <CustomTypography variant="heading6" sx={{ fontWeight: 600 }}>
                    Delivery & Tracking
                  </CustomTypography>
                  {canEditShipments && (canEditStatus || isTerminalStatus) ? (
                    <AppButton
                      label={savingDelivery ? "Saving…" : "Save delivery"}
                      variant="primary"
                      onClick={() => {
                        void handleSaveDelivery();
                      }}
                      disabled={
                        savingDelivery || isDetailsLoading || !canEditStatus
                      }
                      customStyles={sectionCtaStyles}
                      fontSize="0.75rem"
                    />
                  ) : null}
                </Box>
                <CustomTypography
                  variant="body3"
                  style={{ marginBottom: "16px" }}
                >
                  Note: Shipment status can be moved forward only after a box,
                  delivery partner, shipment method and packs have been added.
                  Stock may be added to a shipment with negative quantities as a
                  backfill; however, the shipment cannot be marked as Shipped or
                  Delivered until the stock has been inwarded.
                </CustomTypography>
                {deliveryFeedback ? (
                  <Alert
                    severity={deliveryFeedback.severity}
                    onClose={() => setDeliveryFeedback(null)}
                    sx={{ mb: 1.5 }}
                  >
                    {deliveryFeedback.text}
                  </Alert>
                ) : null}
                <Grid2
                  container
                  spacing={2}
                  columns={{ xs: 12, sm: 12, md: 12 }}
                >
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <DatePicker2
                      label="Expected Delivery Date"
                      type="date"
                      minDate={dayjs()}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: "expected_delivery_date",
                            value: newValue
                              ? newValue.format("YYYY-MM-DD")
                              : "",
                          },
                        } as React.ChangeEvent<HTMLInputElement>);
                      }}
                      value={
                        shipmentDetails?.expected_delivery_date
                          ? dayjs(shipmentDetails.expected_delivery_date)
                          : null
                      }
                      disabled={!canEditDeliveryDates}
                      zIndex={3000}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <CarriersSearchDropdown
                      enabled={open}
                      title="Delivery Partner"
                      fontSize="0.8rem"
                      borderColor={theme.palette.customColors.blackBorder}
                      disabled={!canEditPartnerMethodBooking}
                      value={
                        Number(shipmentDetails?.delivery_partner_id) > 0
                          ? [
                              {
                                id: String(
                                  shipmentDetails?.delivery_partner_id,
                                ),
                                value: String(
                                  shipmentDetails?.delivery_partner_name || "",
                                ),
                              },
                            ]
                          : undefined
                      }
                      handleSelect={(selectedItems) => {
                        if (selectedItems.length === 0) {
                          setShipmentDetails((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  delivery_partner_id: undefined,
                                  delivery_partner_name: "",
                                }
                              : prev,
                          );
                        } else {
                          setShipmentDetails((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  delivery_partner_id: Number(
                                    selectedItems[0].id,
                                  ),
                                  delivery_partner_name: selectedItems[0].value,
                                }
                              : prev,
                          );
                        }
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <AppSearchDropdownMulti
                      borderColor={theme.palette.customColors.blackBorder}
                      isMultiSelect={false}
                      title="Select Shipment Method"
                      fontSize="0.8rem"
                      local
                      disabled={!canEditPartnerMethodBooking}
                      list={SHIPMENT_METHOD_OPTIONS.map((method) => ({
                        id: String(method),
                        value: method,
                      }))}
                      value={
                        shipmentDetails?.shipment_method
                          ? [
                              {
                                id: String(shipmentDetails.shipment_method),
                                value: shipmentDetails.shipment_method,
                              },
                            ]
                          : []
                      }
                      handleSelect={(selectedItems) => {
                        setShipmentDetails((prev) =>
                          prev
                            ? {
                                ...prev,
                                shipment_method: (selectedItems[0]?.value ||
                                  undefined) as ShipmentMethod | undefined,
                              }
                            : prev,
                        );
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <AppSearchDropdownMulti
                      borderColor={theme.palette.customColors.blackBorder}
                      isMultiSelect={false}
                      title="Select Booking Type"
                      fontSize="0.8rem"
                      local
                      disabled={!canEditPartnerMethodBooking}
                      list={SHIPMENT_BOOKING_TYPE_OPTIONS.map((option) => ({
                        id: option.id,
                        value: option.value,
                      }))}
                      value={
                        shipmentDetails?.booking_type
                          ? [
                              {
                                id: String(shipmentDetails.booking_type),
                                value: formatShipmentBookingTypeLabel(
                                  shipmentDetails.booking_type,
                                ),
                              },
                            ]
                          : []
                      }
                      handleSelect={(selectedItems) => {
                        setShipmentDetails((prev) =>
                          prev
                            ? {
                                ...prev,
                                booking_type: parseShipmentBookingType(
                                  selectedItems[0]?.id,
                                ),
                              }
                            : prev,
                        );
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <AppSearchDropdownMulti
                      borderColor={theme.palette.customColors.blackBorder}
                      isMultiSelect={false}
                      title="Select Status"
                      fontSize="0.8rem"
                      value={
                        shipmentDetails?.status
                          ? [
                              {
                                id: String(shipmentDetails.status),
                                value:
                                  statusList.find(
                                    (status) =>
                                      status.id === shipmentDetails.status,
                                  )?.value || "Select Status",
                              },
                            ]
                          : undefined
                      }
                      list={statusDropdownOptions}
                      local={true}
                      handleSelect={(selectedItems) => {
                        if (selectedItems.length === 0) {
                          setShipmentDetails((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  status: -1,
                                  status_label: "",
                                }
                              : prev,
                          );
                        } else {
                          setShipmentDetails((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  status: Number(selectedItems[0].id),
                                  status_label: selectedItems[0].value,
                                }
                              : prev,
                          );
                        }
                      }}
                      disabled={!canEditStatus}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <DatePicker2
                      type="date"
                      minDate={dayjs().subtract(1, "week")}
                      label="Dispatch Date"
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: "dispatch_date",
                            value: newValue
                              ? newValue.format("YYYY-MM-DD HH:mm:ss")
                              : "",
                          },
                        } as React.ChangeEvent<HTMLInputElement>);
                      }}
                      value={
                        shipmentDetails?.dispatch_date
                          ? dayjs(shipmentDetails.dispatch_date)
                          : null
                      }
                      disabled={!canEditDeliveryDates}
                      zIndex={3000}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Shipping Cost"
                      name="shipping_cost"
                      type="number"
                      value={
                        shipmentDetails?.shipping_cost == null
                          ? ""
                          : shipmentDetails.shipping_cost
                      }
                      onChange={handleChange}
                      disabled={!canEditShippingCost}
                      sx={textFieldSx}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Declared Value"
                      name="declared_value"
                      type="number"
                      value={
                        shipmentDetails?.declared_value == null
                          ? ""
                          : shipmentDetails.declared_value
                      }
                      onChange={handleChange}
                      disabled={!canEditDelivery}
                      sx={textFieldSx}
                      slotProps={{
                        htmlInput: { min: 0, step: "any" },
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="AWB Number"
                      name="awb"
                      value={shipmentDetails?.awb || ""}
                      onChange={handleChange}
                      disabled={!canEditDeliveryAwb}
                      sx={textFieldSx}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        label="Tracking Link"
                        name="awb_track_url"
                        value={shipmentDetails?.awb_track_url || ""}
                        onChange={handleChange}
                        disabled={!canEditDeliveryAwb}
                        sx={textFieldSx}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="Copy tracking link"
                                  onClick={handleCopyTrackingLink}
                                  disabled={
                                    !shipmentDetails?.awb_track_url?.trim()
                                  }
                                  edge="end"
                                  sx={{
                                    p: { xs: 0.75, sm: 1 },
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.action.hover,
                                    },
                                  }}
                                >
                                  <ContentCopyIcon
                                    sx={{
                                      fontSize: { xs: "1rem", sm: "1.25rem" },
                                    }}
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                      {linkCopied && (
                        <CustomTypography
                          variant="body3"
                          sx={{
                            color:
                              theme.palette.customColors?.successGreen ||
                              theme.palette.success.main,
                            mt: 0.5,
                            display: "block",
                          }}
                        >
                          Tracking link copied!
                        </CustomTypography>
                      )}
                      {copyError ? (
                        <CustomTypography
                          variant="body3"
                          sx={{
                            color:
                              theme.palette.customColors?.errorRed ||
                              theme.palette.error.main,
                            mt: 0.5,
                            display: "block",
                          }}
                        >
                          {copyError}
                        </CustomTypography>
                      ) : null}
                    </Box>
                  </Grid2>
                </Grid2>
              </Box>

              {showCarrierBooking ? (
                <>
                  <Divider sx={{ my: 3 }} />

                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 1.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <CustomTypography
                        variant="heading6"
                        sx={{ fontWeight: 600 }}
                      >
                        Carrier Booking
                      </CustomTypography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        {canSyncTracking ? (
                          <AppButton
                            label={
                              syncingTracking ? "Queuing…" : "Sync Tracking"
                            }
                            variant="transparentPrimary"
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                              void handleSyncTracking();
                            }}
                            disabled={
                              syncingTracking ||
                              isDetailsLoading ||
                              isTrackingTerminalStatus(persistedStatus)
                            }
                            customStyles={sectionCtaStyles}
                            fontSize="0.75rem"
                          />
                        ) : null}
                        <AppButton
                          label={
                            syncingBooking
                              ? "Queuing…"
                              : requeueStaleInProgress
                                ? "Re-queue Booking"
                                : "Sync Booking"
                          }
                          variant="primary"
                          onClick={() => {
                            void handleSyncBooking();
                          }}
                          disabled={
                            syncingBooking ||
                            isDetailsLoading ||
                            isStatusOnlyLock ||
                            isTerminalStatus
                          }
                          customStyles={sectionCtaStyles}
                          fontSize="0.75rem"
                        />
                      </Box>
                    </Box>

                    {bookingFeedback ? (
                      <Alert
                        severity={bookingFeedback.severity}
                        onClose={() => setBookingFeedback(null)}
                        sx={{ mb: 1.5 }}
                      >
                        {bookingFeedback.text}
                      </Alert>
                    ) : null}

                    {trackingFeedback ? (
                      <Alert
                        severity={trackingFeedback.severity}
                        onClose={() => setTrackingFeedback(null)}
                        sx={{ mb: 1.5 }}
                      >
                        {trackingFeedback.text}
                      </Alert>
                    ) : null}

                    {showLastBookingError ? (
                      <Alert severity="error" sx={{ mb: 1.5 }}>
                        {details?.last_booking_error}
                      </Alert>
                    ) : null}

                    <Grid2 container spacing={2}>
                      <Grid2 size={{ xs: 6, sm: 4 }}>
                        <CustomTypography
                          variant="body3"
                          sx={{
                            mb: 0.5,
                            color: theme.palette.customColors?.mediumGrayText,
                          }}
                        >
                          Booking status
                        </CustomTypography>
                        {details?.booking_state ? (
                          <Chip
                            label={details.booking_state}
                            color={getBookingStateChipColor(
                              details.booking_state,
                            )}
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        ) : (
                          <CustomTypography variant="body2">—</CustomTypography>
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 6, sm: 4 }}>
                        <CustomTypography
                          variant="body3"
                          sx={{
                            mb: 0.5,
                            color: theme.palette.customColors?.mediumGrayText,
                          }}
                        >
                          Last booked at
                        </CustomTypography>
                        <CustomTypography variant="body2">
                          {formatBookingDate(details?.last_booked_at)}
                        </CustomTypography>
                      </Grid2>
                      <Grid2 size={{ xs: 6, sm: 4 }}>
                        <CustomTypography
                          variant="body3"
                          sx={{
                            mb: 0.5,
                            color: theme.palette.customColors?.mediumGrayText,
                          }}
                        >
                          Carrier ref
                        </CustomTypography>
                        <CustomTypography
                          variant="body2"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {details?.carrier_ref_no || "—"}
                        </CustomTypography>
                      </Grid2>
                      <Grid2 size={{ xs: 6, sm: 4 }}>
                        <CustomTypography
                          variant="body3"
                          sx={{
                            mb: 0.5,
                            color: theme.palette.customColors?.mediumGrayText,
                          }}
                        >
                          Booking attempts
                        </CustomTypography>
                        <CustomTypography variant="body2">
                          {details?.booking_attempts == null
                            ? "—"
                            : String(details.booking_attempts)}
                        </CustomTypography>
                      </Grid2>
                      <Grid2 size={{ xs: 6, sm: 4 }}>
                        <CustomTypography
                          variant="body3"
                          sx={{
                            mb: 0.5,
                            color: theme.palette.customColors?.mediumGrayText,
                          }}
                        >
                          Shipping label
                        </CustomTypography>
                        <LabelSecondary
                          labelS3Key={details?.label_s3_key}
                          emptyFallback="—"
                          fontSize="0.75rem"
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 6, sm: 4 }}>
                        <CustomTypography
                          variant="body3"
                          sx={{
                            mb: 0.5,
                            color: theme.palette.customColors?.mediumGrayText,
                          }}
                        >
                          MPS Shipping label
                        </CustomTypography>
                        <LabelSecondary
                          labelS3Key={details?.mps_label_s3_key}
                          emptyFallback="—"
                          fontSize="0.75rem"
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12 }}>
                        <CustomTypography
                          variant="body3"
                          sx={{
                            mb: 0.5,
                            color: theme.palette.customColors?.mediumGrayText,
                          }}
                        >
                          Last booking error
                        </CustomTypography>
                        <CustomTypography
                          variant="body2"
                          sx={{
                            wordBreak: "break-word",
                            color: details?.last_booking_error
                              ? theme.palette.customColors?.errorRed ||
                                theme.palette.error.main
                              : undefined,
                          }}
                        >
                          {details?.last_booking_error || "—"}
                        </CustomTypography>
                      </Grid2>
                    </Grid2>
                  </Box>
                </>
              ) : null}

              <Divider sx={{ my: 3 }} />

              {shipmentId != null && details && (
                <Box sx={{ mb: 3 }}>
                  <ShipmentProductsSection
                    shipmentId={shipmentId}
                    products={details.products}
                    inventoryId={details.inventory_id}
                    canAdd={Boolean(canAddItems)}
                    canRemove={Boolean(canRemoveProducts)}
                    shipmentsAPI={api}
                    headingVariant="heading6"
                    onProductsChange={(nextProducts) => {
                      try {
                        setDetails((prev) =>
                          prev ? { ...prev, products: nextProducts } : prev,
                        );
                      } catch {
                        // Product list stays as last successful API payload.
                      }
                    }}
                  />
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {shipmentId != null && details && (
                <Box>
                  <ShipmentBoxesSection
                    shipmentId={shipmentId}
                    boxes={details.boxes}
                    canEdit={Boolean(canEditBoxes)}
                    shipmentsAPI={api}
                    onBoxesChange={(nextBoxes) => {
                      try {
                        setDetails((prev) =>
                          prev ? { ...prev, boxes: nextBoxes } : prev,
                        );
                      } catch {
                        // Box list stays as last successful API payload.
                      }
                    }}
                  />
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <CustomTypography variant="heading6" sx={{ fontWeight: 600 }}>
                    Recipient Information
                  </CustomTypography>
                  {canEditShipments &&
                  persistedStatus === ShipmentStatusEnum.NEW ? (
                    <AppButton
                      label={savingRecipient ? "Saving…" : "Save recipient"}
                      variant="primary"
                      onClick={() => {
                        void handleSaveRecipient();
                      }}
                      disabled={
                        savingRecipient || isDetailsLoading || isStatusOnlyLock
                      }
                      customStyles={sectionCtaStyles}
                      fontSize="0.75rem"
                    />
                  ) : null}
                </Box>
                {recipientFeedback ? (
                  <Alert
                    severity={recipientFeedback.severity}
                    onClose={() => setRecipientFeedback(null)}
                    sx={{ mb: 1.5 }}
                  >
                    {recipientFeedback.text}
                  </Alert>
                ) : null}
                <Grid2
                  container
                  spacing={2}
                  columns={{ xs: 12, sm: 12, md: 12 }}
                >
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="First Name"
                      name="first_name"
                      value={shipmentDetails?.first_name}
                      onChange={handleChange}
                      disabled={!canEditRecipient}
                      sx={textFieldSx}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Last Name"
                      name="last_name"
                      value={shipmentDetails?.last_name}
                      onChange={handleChange}
                      disabled={!canEditRecipient}
                      sx={textFieldSx}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      name="email"
                      type="email"
                      value={shipmentDetails?.email}
                      onChange={handleChange}
                      disabled={!canEditRecipient}
                      sx={textFieldSx}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone Number"
                      name="phone"
                      value={shipmentDetails?.phone}
                      onChange={handleChange}
                      disabled={!canEditRecipient}
                      sx={textFieldSx}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Alternate Number"
                      name="alternate_phone"
                      value={shipmentDetails?.alternate_phone}
                      onChange={handleChange}
                      disabled={!canEditRecipient}
                      sx={textFieldSx}
                    />
                  </Grid2>
                </Grid2>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <CustomTypography variant="heading6" sx={{ fontWeight: 600 }}>
                    Shipping Address
                  </CustomTypography>
                  {canEditShipments &&
                  persistedStatus === ShipmentStatusEnum.NEW ? (
                    <AppButton
                      label={savingAddress ? "Saving…" : "Save address"}
                      variant="primary"
                      onClick={() => {
                        void handleSaveAddress();
                      }}
                      disabled={
                        savingAddress || isDetailsLoading || isStatusOnlyLock
                      }
                      customStyles={sectionCtaStyles}
                      fontSize="0.75rem"
                    />
                  ) : null}
                </Box>
                {addressFeedback ? (
                  <Alert
                    severity={addressFeedback.severity}
                    onClose={() => setAddressFeedback(null)}
                    sx={{ mb: 1.5 }}
                  >
                    {addressFeedback.text}
                  </Alert>
                ) : null}
                <Grid2
                  container
                  spacing={2}
                  columns={{ xs: 12, sm: 12, md: 12 }}
                >
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Country"
                      name="country"
                      value={shipmentDetails?.country}
                      onChange={handleChange}
                      disabled={!canEditAddress}
                      sx={textFieldSx}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="State"
                      name="state"
                      value={shipmentDetails?.state}
                      onChange={handleChange}
                      disabled={!canEditAddress}
                      sx={textFieldSx}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      name="city"
                      value={shipmentDetails?.city}
                      onChange={handleChange}
                      disabled={!canEditAddress}
                      sx={textFieldSx}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Pincode"
                      name="pincode"
                      value={shipmentDetails?.pincode}
                      onChange={handleChange}
                      disabled={!canEditAddress}
                      sx={textFieldSx}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Shipping Address 1"
                      name="address1"
                      value={shipmentDetails?.address1}
                      onChange={handleChange}
                      disabled={!canEditAddress}
                      sx={textFieldSx}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Shipping Address 2"
                      name="address2"
                      value={shipmentDetails?.address2}
                      onChange={handleChange}
                      disabled={!canEditAddress}
                      sx={textFieldSx}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Landmark"
                      name="landmark"
                      value={shipmentDetails?.landmark}
                      onChange={handleChange}
                      disabled={!canEditAddress}
                      sx={textFieldSx}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Order Notes"
                      name="order_notes"
                      multiline
                      minRows={2}
                      value={shipmentDetails?.order_notes}
                      onChange={handleChange}
                      disabled={!canEditAddress}
                      sx={textFieldSx}
                    />
                  </Grid2>
                </Grid2>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${theme.palette.customColors?.greyBorder || "#e0e0e0"}`,
            backgroundColor: theme.palette.customColors?.lightGreyColor,
            flexDirection: "column",
            alignItems: "stretch",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <AppButton
              label="Close"
              variant="secondary"
              onClick={handleClose}
              disabled={isAnySectionSaving}
              customStyles={{ minHeight: 40, px: 3 }}
            />
          </Box>
        </DialogActions>
      </Box>

    </Dialog>
  );
};

export default EditShipmentModal;
